import React, { useRef, useState, useEffect, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTheme } from '../../../theme/ThemeContext';
import { createBenchmarkChartOptions } from './benchmarkChartHelpers';
import { Maximize2, Minimize2, RotateCcw } from 'lucide-react';

interface EChartsBenchmarkChartProps {
  data: any[];
  selectedVariable: string;
  mode: 'absolute' | 'skill';
  referenceModel?: string;
}

export function EChartsBenchmarkChart({
  data,
  selectedVariable,
  mode,
  referenceModel,
}: EChartsBenchmarkChartProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const chartRef = useRef<ReactECharts>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [previousOption, setPreviousOption] = useState<any>(null);

  // Handle fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (chartRef.current?.getEchartsInstance()) {
        chartRef.current.getEchartsInstance().resize();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Calculate chart options only when input data changes
  const option = useMemo(() => {
    if (!selectedVariable || data.length === 0) {
      return null;
    }

    // Generate options for ECharts
    return createBenchmarkChartOptions(
      data,
      selectedVariable,
      mode,
      isDark,
      mode === 'skill' ? referenceModel : undefined,
    );
  }, [data, selectedVariable, mode, referenceModel, isDark]);

  // Effect to update the previous option whenever we have a valid new option
  useEffect(() => {
    if (option && option.series && option.series.length > 0) {
      setPreviousOption(option);
    }
  }, [option]);

  // Reset zoom function
  const resetZoom = () => {
    if (chartRef.current?.getEchartsInstance()) {
      chartRef.current.getEchartsInstance().dispatchAction({
        type: 'dataZoom',
        start: 0,
        end: 100,
      });
    }
  };

  // Toggle fullscreen function
  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  if (!selectedVariable || data.length === 0) {
    return (
      <div className="h-[500px] flex items-center justify-center text-gray-500">
        Select a variable and models to display the chart
      </div>
    );
  }

  // Use current option if valid, otherwise fall back to previous option if available
  const chartOption = option && option.series.length > 0 ? option : previousOption;

  // If we don't have any valid option, show an empty state
  if (!chartOption || !chartOption.series || chartOption.series.length === 0) {
    return (
      <div className="h-[500px] flex items-center justify-center text-gray-500">
        No valid data available for the selected variable
      </div>
    );
  }

  // Create a unique key for the chart to force remounting when key props change
  // Using the option series length ensures we don't remount unless the actual data changed
  const chartKey = `${selectedVariable}-${mode}-${referenceModel || 'none'}-${chartOption.series.length}`;

  return (
    <div
      className={`relative ${isFullscreen ? (isDark ? 'bg-theme-dark-bg' : 'bg-white') : ''}`}
      ref={containerRef}
    >
      <div className="absolute right-2 top-2 z-[5] flex gap-2">
        <button
          onClick={resetZoom}
          className="rounded bg-white dark:bg-gray-700 p-1 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 shadow-sm"
          title="Reset zoom"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
        <button
          onClick={toggleFullscreen}
          className="rounded bg-white dark:bg-gray-700 p-1 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 shadow-sm"
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
        </button>
      </div>
      <div
        className={`overflow-x-auto min-h-[500px] -mx-2 sm:mx-0 ${isFullscreen ? 'h-screen' : ''}`}
      >
        <div className={`min-w-[800px] ${isFullscreen ? 'h-screen' : 'h-[500px]'}`}>
          <ReactECharts
            key={chartKey}
            ref={chartRef}
            option={chartOption}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'canvas' }}
            notMerge={true}
            lazyUpdate={false}
          />
        </div>
      </div>
    </div>
  );
}
